import { useNavigate, useParams, useRouter } from "@tanstack/react-router";
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  BarChart3Icon,
  ExternalLinkIcon,
  ListIcon,
  MinusIcon,
  SaveIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useProductFullQuery } from "@/modules/catalog/presentation/api/queries";
import { qtyToBase } from "@/modules/inventory/domain/lib/unit-conversion";
import type {
  IngredientListRow,
  RecipeItemPayload,
} from "@/modules/inventory/domain/types";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { Loader } from "@/shared/ui/Loader";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { inventoryIngredientsApi } from "../api/client";
import { useIngredientCategoriesLookupQuery } from "../api/ingredient-categories-queries";
import { useRecipeByVariantId, useUpsertRecipe } from "../api/queries";

type ItemRow = RecipeItemPayload & {
  ingredientName?: string;
  baseUnit?: string;
  toBaseFactor?: number;
};

const ROUNDING_NONE = "__none__" as const;
const ROUNDING_OPTIONS = [
  {
    value: ROUNDING_NONE,
    label: "–",
    icon: MinusIcon,
    title: "ບໍ່ປັດ",
    description: "ໃຊ້ຄ່າຕາມທี่ຄຳນວນໄດ້ ບໍ່ປັດເສດ",
  },
  {
    value: "round_up",
    label: "ປັດຂຶ້ນ (round up)",
    icon: ArrowUpIcon,
    title: "ປັດຂຶ້ນ",
    description: "ປັດຂຶ້ນເປັນຈຳນວນເຕັມ ເມື່ອມີທົດສະຍົມ",
  },
  {
    value: "round_down",
    label: "ປັດລົງ (round down)",
    icon: ArrowDownIcon,
    title: "ປັດລົງ",
    description: "ປັດລົງເປັນຈຳນວນເຕັມ ເມື່ອມີທົດສະຍົມ",
  },
];

export function RecipeEditPage() {
  const { variantId } = useParams({
    from: "/app/inventory/recipes/$variantId/edit",
  });
  const nav = useNavigate({
    from: "/app/inventory/recipes/$variantId/edit",
  });
  const router = useRouter();
  const id = variantId ?? "";

  const recipeQuery = useRecipeByVariantId(id);
  const upsertRecipe = useUpsertRecipe(id);

  const [yieldQty, setYieldQty] = useState(1);
  const [wastePct, setWastePct] = useState<number | null>(null);
  const [roundingRule, setRoundingRule] = useState<string | null>(null);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [addIngredientId, setAddIngredientId] = useState("");
  const [pickerCategoryId, setPickerCategoryId] = useState("all");
  const categoriesLookup = useIngredientCategoriesLookupQuery();

  const data = recipeQuery.data;
  const productId = data?.variant?.productId ?? "";
  const productQuery = useProductFullQuery(productId);

  useEffect(() => {
    if (!data) return;
    setYieldQty(data.recipe?.yieldQty ?? 1);
    setWastePct(data.recipe?.wastePct ?? null);
    setRoundingRule(data.recipe?.roundingRule ?? null);
    setItems(
      data.items.map((r) => ({
        ingredientId: r.ingredientId,
        qty: r.qty,
        unit: r.unit,
        ingredientName: r.ingredientName,
        baseUnit: r.baseUnit,
        toBaseFactor: r.toBaseFactor,
      })),
    );
  }, [
    data?.recipe?.id,
    data?.recipe?.yieldQty,
    data?.recipe?.wastePct,
    data?.recipe?.roundingRule,
    data?.items,
  ]);

  const addItem = useCallback(async () => {
    if (!addIngredientId) return;
    let ing: IngredientListRow;
    try {
      ing = await inventoryIngredientsApi.get(addIngredientId);
    } catch {
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        ingredientId: ing.id,
        qty: 0,
        unit: ing.issueUnit,
        ingredientName: ing.name,
        baseUnit: ing.baseUnit,
        toBaseFactor: ing.issueToBase,
      },
    ]);
    setAddIngredientId("");
  }, [addIngredientId]);

  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateItemQty = useCallback((index: number, qty: number) => {
    setItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, qty } : row)),
    );
  }, []);

  const handleSave = async () => {
    if (items.length === 0) {
      toast.error("ຕ້ອງມີວັດຖຸດິບຢ່າງໜ້ອຍ 1 ລາຍການ");
      return;
    }
    const invalid = items.some((r) => r.qty <= 0);
    if (invalid) {
      toast.error("ປະລິມານຕ້ອງໃຫຍ່ກວ່າ 0");
      return;
    }
    const payload: RecipeItemPayload[] = items.map((r) => ({
      ingredientId: r.ingredientId,
      qty: r.qty,
      unit: r.unit,
    }));
    try {
      await upsertRecipe.mutateAsync({
        yieldQty,
        wastePct,
        roundingRule,
        items: payload,
      });
      toast.success("ບັນທຶກສູດສຳເລັດ");
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? "ບັນທຶກສູດລົ້ມເຫຼວ");
    }
  };

  const handleDiscard = () => {
    if (data) {
      setYieldQty(data.recipe?.yieldQty ?? 1);
      setWastePct(data.recipe?.wastePct ?? null);
      setRoundingRule(data.recipe?.roundingRule ?? null);
      setItems(
        data.items.map((r) => ({
          ingredientId: r.ingredientId,
          qty: r.qty,
          unit: r.unit,
          ingredientName: r.ingredientName,
          baseUnit: r.baseUnit,
          toBaseFactor: r.toBaseFactor,
        })),
      );
    }
  };

  if (recipeQuery.isLoading || !data) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ສິນຄ້າ / ເມນູ",
                to: "/app/catalog/products",
              },
              {
                label: "ແກ້ໄຂສູດ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader />
          </div>
        </Main>
      </>
    );
  }

  const variant = data.variant;
  const variants = productQuery.data?.variants ?? [];
  const variantIndex = (() => {
    const i = variants.findIndex((v) => v.id === variant.id);
    return i >= 0 ? i + 1 : 1;
  })();
  const variantTotal = Math.max(variants.length, 1);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ສິນຄ້າ / ເມນູ",
              to: "/app/catalog/products",
            },
            {
              label: "ແກ້ໄຂສູດ",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <h2 className="font-bold text-2xl tracking-tight">
                ແກ້ໄຂສູດ: {variant.productName} – {variant.name}
              </h2>
              <p className="text-muted-foreground text-sm">
                ກຳນົດປະລິມານວັດຖຸດິບ, ເປີ້ນເງິນຂີ້ເຫຍື່ອ ແລະ ຜົນໄດ້ຮັບມາດຕະຖານ ສຳລັບຕົວແບບນີ້.
                (ຕົວແບບທີ່ {variantIndex} ຈາກ {variantTotal})
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => router.history.back()}>
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            <Button variant="outline" onClick={handleDiscard}>
              <XIcon className="size-4" />
              ຍົກເລີກ
            </Button>
            <Button onClick={handleSave} isLoading={upsertRecipe.isPending}>
              <SaveIcon className="size-4" />
              ບັນທຶກສູດ
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="flex flex-col gap-6">
            <Card className="rounded-xl border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading text-lg">
                  <BarChart3Icon className="size-5 text-muted-foreground" />
                  ຫົວຂໍ້ສູດ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="recipe-yield"
                      className="mb-2 block font-medium text-sm"
                    >
                      ຜົນໄດ້ຮັບມາດຕະຖານ (ຫນ່ວຍ)
                    </label>
                    <Input
                      id="recipe-yield"
                      type="number"
                      min={0.01}
                      step={0.1}
                      value={yieldQty}
                      onChange={(e) =>
                        setYieldQty(Number.parseFloat(e.target.value) || 0)
                      }
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="recipe-waste"
                      className="mb-2 block font-medium text-sm"
                    >
                      ອັດຕາຂອງເສດເຫຼືອ (%)
                    </label>
                    <Input
                      id="recipe-waste"
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={wastePct ?? ""}
                      placeholder="–"
                      onChange={(e) => {
                        const v = e.target.value;
                        setWastePct(
                          v === "" ? null : Number.parseFloat(v) || null,
                        );
                      }}
                    />
                  </div>
                </div>
                <div>
                  <span className="mb-3 block font-medium text-sm">
                    ກົດປັດເສດ (ເລືອກໄດ້)
                  </span>
                  <RadioGroup
                    value={roundingRule ?? ROUNDING_NONE}
                    onValueChange={(v) =>
                      setRoundingRule(v === ROUNDING_NONE ? null : v || null)
                    }
                    className="grid gap-2 sm:grid-cols-3"
                  >
                    {ROUNDING_OPTIONS.map((opt) => {
                      const id = `recipe-rounding-${opt.value}`;
                      const selected =
                        (roundingRule ?? ROUNDING_NONE) === opt.value;
                      const Icon = opt.icon;
                      return (
                        <label
                          key={opt.value}
                          htmlFor={id}
                          className={[
                            "flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors",
                            "hover:bg-muted/50",
                            selected
                              ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                              : "border-input bg-card",
                          ].join(" ")}
                        >
                          <RadioGroupItem
                            value={opt.value}
                            id={id}
                            className="mt-0.5 shrink-0"
                            hidden
                          />
                          <Icon className="size-5 shrink-0 text-muted-foreground" />
                          <div className="min-w-0 flex-1 space-y-0.5">
                            <p className="font-medium text-sm">{opt.title}</p>
                            <p className="text-muted-foreground text-xs leading-snug">
                              {opt.description}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl border bg-card">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 font-heading text-lg">
                    <ListIcon className="size-5 text-muted-foreground" />
                    ລາຍການວັດຖຸດິບ
                  </CardTitle>
                  <span className="text-muted-foreground text-sm">
                    {items.length} ວັດຖຸດິບ
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2 text-left font-medium">
                          ວັດຖຸດິບ
                        </th>
                        <th className="px-4 py-2 text-left font-medium">
                          ປະລິມານ
                        </th>
                        <th className="px-4 py-2 text-left font-medium">
                          ຫນ່ວຍ
                        </th>
                        <th className="w-12 px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row, index) => (
                        <tr
                          key={`${row.ingredientId}-${index}`}
                          className="border-b"
                        >
                          <td className="px-4 py-2">
                            <span className="font-medium">
                              {row.ingredientName ?? row.ingredientId}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              className="w-24"
                              value={row.qty}
                              onChange={(e) =>
                                updateItemQty(
                                  index,
                                  Number.parseFloat(e.target.value) || 0,
                                )
                              }
                            />
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">
                            <div>{row.unit}</div>
                            {row.baseUnit ? (
                              <div className="text-xs">
                                ={" "}
                                {qtyToBase(
                                  row.qty,
                                  row.toBaseFactor ?? 1,
                                  row.baseUnit ?? row.unit,
                                  row.unit,
                                ).toLocaleString()}{" "}
                                {row.baseUnit} ຕໍ່ yield
                              </div>
                            ) : null}
                          </td>
                          <td className="px-2 py-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(index)}
                            >
                              <TrashIcon className="size-4 text-destructive" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <SimpleSelect
                    placeholder="ຫມວດ"
                    value={pickerCategoryId}
                    onValueChange={(v) => {
                      setPickerCategoryId(v);
                      setAddIngredientId("");
                    }}
                    options={[
                      { value: "all", label: "ທຸກຫມວດ" },
                      { value: "__uncategorized__", label: "ຍັງບໍ່ຈັດຫມວດ" },
                      ...(categoriesLookup.data ?? []).map((c) => ({
                        value: c.id,
                        label: c.name,
                      })),
                    ]}
                    className="w-full sm:w-48"
                  />
                  <InfiniteCombobox<IngredientListRow>
                    queryKey={[
                      "inventory",
                      "ingredients",
                      "lookup",
                      pickerCategoryId,
                    ]}
                    queryFn={({ search, pageParam }) =>
                      inventoryIngredientsApi.lookup({
                        search,
                        page: pageParam,
                        limit: 20,
                        categoryId:
                          pickerCategoryId === "all"
                            ? undefined
                            : pickerCategoryId,
                      })
                    }
                    preloadQueryFn={(id) =>
                      inventoryIngredientsApi.get(id).catch(() => null)
                    }
                    getLabel={(ing) => {
                      const cat = ing.categoryName
                        ? ` · ${ing.categoryName}`
                        : "";
                      return `${ing.name} (${ing.issueUnit})${cat}`;
                    }}
                    getValue={(ing) => ing.id}
                    getDisabled={(ing) =>
                      items.some((i) => i.ingredientId === ing.id)
                    }
                    value={addIngredientId}
                    onValueChange={(v) => setAddIngredientId(v ?? "")}
                    placeholder="ເລືອກວັດຖຸດິບເພີ່ມ..."
                    className="flex-1"
                    clearable
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addItem}
                    disabled={!addIngredientId}
                  >
                    ເພີ່ມວັດຖຸດິບ
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-6">
            <Card className="overflow-hidden rounded-xl border bg-card py-0 shadow-sm">
              <div className="relative">
                <div className="absolute top-4 right-4">
                  <Badge variant="secondary">
                    ຕົວແບບທີ່ {variantIndex} ຈາກ {variantTotal}
                  </Badge>
                </div>
                {variant.imageUrl ? (
                  <img
                    src={resolveImageSrc(variant.imageUrl) ?? ""}
                    alt={variant.productName}
                    className="mb-4 aspect-square w-full rounded-lg border object-cover"
                  />
                ) : (
                  <div className="mb-4 flex aspect-square w-full items-center justify-center rounded-lg border bg-muted text-muted-foreground text-sm">
                    ບໍ່ມີຮູບ
                  </div>
                )}
                <div className="px-4 pb-4">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="font-heading font-semibold text-lg">
                      {variant.productName}
                    </CardTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      title="ໄປແກ້ໄຂສິນຄ້າ"
                      onClick={() =>
                        nav({
                          to: "/app/catalog/products/$id/edit",
                          params: { id: variant.productId },
                        })
                      }
                    >
                      <ExternalLinkIcon className="size-4" />
                    </Button>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        SKU
                      </p>
                      <p className="font-semibold">{variant.sku ?? "–"}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        ລາຄາ
                      </p>
                      <p className="font-semibold">
                        {variant.basePriceLak.toLocaleString("en-US")} ກີບ
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Main>
    </>
  );
}
